/**
 * Sounds for the on-screen announcements: a cheering crowd for a goal, the
 * referee's whistle for cards and penalties, a groan for a missed penalty.
 *
 * Synthesised rather than shipped as samples, so there is nothing to license.
 * Each sound is rendered once in an OfflineAudioContext and played through a
 * plain <audio> element: a live AudioContext next to the videos is what made
 * the TV drop frames, and a finished WAV needs none.
 */

export type SoundKind = 'goal' | 'whistleShort' | 'whistleLong' | 'groan';

const RATE = 44_100;
/**
 * Target RMS per sound, so they sit at a similar loudness: a whistle at 3 kHz
 * sounds far louder than a crowd at the same level, so it gets less.
 */
const LOUDNESS: Record<SoundKind, number> = { goal: 0.16, groan: 0.13, whistleShort: 0.08, whistleLong: 0.08 };
const urls = new Map<SoundKind, Promise<string | null>>();

/** Plays `kind` at `volume` (0..1); silently does nothing where audio is unavailable. */
export function playSound(kind: SoundKind, volume: number): void {
  if (volume <= 0) return;
  void urlFor(kind).then((url) => {
    if (!url) return;
    const audio = new Audio(url);
    audio.volume = Math.min(1, volume);
    // Autoplay rules may refuse before the first interaction; nothing to do then.
    audio.play().catch(() => {});
  });
}

function urlFor(kind: SoundKind): Promise<string | null> {
  let url = urls.get(kind);
  if (!url) {
    url = render(kind).then((buffer) => URL.createObjectURL(toWav(buffer, LOUDNESS[kind]))).catch(() => null);
    urls.set(kind, url);
  }
  return url;
}

function render(kind: SoundKind): Promise<AudioBuffer> {
  const seconds = { goal: 4.5, whistleShort: 0.45, whistleLong: 1.2, groan: 2.2 }[kind];
  const ctx = new OfflineAudioContext(1, Math.ceil(RATE * seconds), RATE);
  if (kind === 'goal') crowd(ctx, seconds, 'cheer');
  else if (kind === 'groan') crowd(ctx, seconds, 'groan');
  else whistle(ctx, kind === 'whistleLong' ? 1.0 : 0.3);
  return ctx.startRendering();
}

function noise(ctx: BaseAudioContext, seconds: number): AudioBufferSourceNode {
  const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * seconds), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  // Pinkish: white noise through a one-pole lowpass, closer to a crowd than hiss.
  let last = 0;
  for (let i = 0; i < data.length; i++) {
    last = 0.97 * last + 0.03 * (Math.random() * 2 - 1);
    data[i] = last * 6;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  return src;
}

/**
 * A pea whistle: a high tone that the rattling pea modulates in pitch and
 * loudness, with some breath noise on top.
 */
function whistle(ctx: OfflineAudioContext, length: number): void {
  const out = ctx.createGain();
  out.connect(ctx.destination);
  const t0 = 0.02;
  out.gain.setValueAtTime(0, 0);
  out.gain.linearRampToValueAtTime(0.5, t0 + 0.02);
  out.gain.setValueAtTime(0.5, t0 + length - 0.06);
  out.gain.linearRampToValueAtTime(0, t0 + length);

  const tone = ctx.createOscillator();
  tone.frequency.value = 2900;
  const rattle = ctx.createOscillator();
  rattle.frequency.value = 34;
  const depth = ctx.createGain();
  depth.gain.value = 180;
  rattle.connect(depth).connect(tone.frequency);

  const trem = ctx.createGain();
  trem.gain.value = 0.75;
  const tremDepth = ctx.createGain();
  tremDepth.gain.value = 0.25;
  rattle.connect(tremDepth).connect(trem.gain);
  tone.connect(trem).connect(out);

  // The second partial gives it the shrill edge.
  const overtone = ctx.createOscillator();
  overtone.frequency.value = 5800;
  depth.connect(overtone.frequency);
  const overGain = ctx.createGain();
  overGain.gain.value = 0.12;
  overtone.connect(overGain).connect(trem);

  const breath = noise(ctx, length + 0.1);
  const band = ctx.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.value = 3000;
  band.Q.value = 2;
  const breathGain = ctx.createGain();
  breathGain.gain.value = 0.06;
  breath.connect(band).connect(breathGain).connect(out);

  for (const node of [tone, rattle, overtone, breath]) {
    node.start(0);
    node.stop(t0 + length + 0.05);
  }
}

/**
 * A stadium: broadband noise for the mass of voices plus a few dozen detuned
 * "aah" voices through vowel formants. A cheer swells and holds; a groan
 * starts high and sinks.
 */
function crowd(ctx: OfflineAudioContext, seconds: number, mood: 'cheer' | 'groan'): void {
  const out = ctx.createGain();
  out.connect(ctx.destination);
  const g = out.gain;
  g.setValueAtTime(0, 0);
  if (mood === 'cheer') {
    g.linearRampToValueAtTime(0.9, 0.35);
    g.setValueAtTime(0.9, 2.0);
    g.exponentialRampToValueAtTime(0.25, 3.4);
    g.linearRampToValueAtTime(0, seconds);
  } else {
    g.linearRampToValueAtTime(0.7, 0.15);
    g.exponentialRampToValueAtTime(0.2, 1.5);
    g.linearRampToValueAtTime(0, seconds);
  }

  const roar = noise(ctx, seconds);
  const roarFilter = ctx.createBiquadFilter();
  roarFilter.type = 'bandpass';
  roarFilter.Q.value = 0.7;
  roarFilter.frequency.setValueAtTime(mood === 'cheer' ? 900 : 1100, 0);
  roarFilter.frequency.linearRampToValueAtTime(mood === 'cheer' ? 1300 : 500, seconds);
  const roarGain = ctx.createGain();
  roarGain.gain.value = 0.5;
  roar.connect(roarFilter).connect(roarGain).connect(out);
  roar.start(0);

  // Vowel formants of a shouted "ah".
  const voices = ctx.createGain();
  voices.gain.value = 0.045;
  for (const [freq, q, gain] of [[750, 6, 1], [1200, 8, 0.6], [2500, 10, 0.25]] as const) {
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = freq;
    f.Q.value = q;
    const fg = ctx.createGain();
    fg.gain.value = gain;
    voices.connect(f).connect(fg).connect(out);
  }
  for (let i = 0; i < 36; i++) {
    const v = ctx.createOscillator();
    v.type = 'sawtooth';
    const base = 170 + Math.random() * 260;
    const start = Math.random() * 0.3;
    v.frequency.setValueAtTime(base * (mood === 'groan' ? 1.25 : 0.9), start);
    v.frequency.linearRampToValueAtTime(base * (mood === 'groan' ? 0.7 : 1.15), start + 0.6 + Math.random() * 0.8);
    // Everyone shouts with their own wobble.
    const vib = ctx.createOscillator();
    vib.frequency.value = 4 + Math.random() * 3;
    const vibDepth = ctx.createGain();
    vibDepth.gain.value = base * 0.02;
    vib.connect(vibDepth).connect(v.frequency);
    v.connect(voices);
    v.start(start);
    vib.start(start);
    const end = mood === 'cheer' ? 2.2 + Math.random() * 1.6 : 1.0 + Math.random() * 0.8;
    v.stop(Math.min(seconds, end));
    vib.stop(Math.min(seconds, end));
  }
}

/** 16-bit mono PCM WAV, scaled to `rms` but never past a peak of 0.95. */
function toWav(buffer: AudioBuffer, rms: number): Blob {
  const data = buffer.getChannelData(0);
  let peak = 0;
  let sum = 0;
  for (const s of data) {
    peak = Math.max(peak, Math.abs(s));
    sum += s * s;
  }
  const current = Math.sqrt(sum / data.length);
  const scale = current > 0 ? Math.min(rms / current, 0.95 / peak) : 1;
  const bytes = new DataView(new ArrayBuffer(44 + data.length * 2));
  const text = (offset: number, s: string) => { for (let i = 0; i < s.length; i++) bytes.setUint8(offset + i, s.charCodeAt(i)); };
  text(0, 'RIFF');
  bytes.setUint32(4, 36 + data.length * 2, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  bytes.setUint32(16, 16, true);
  bytes.setUint16(20, 1, true);
  bytes.setUint16(22, 1, true);
  bytes.setUint32(24, buffer.sampleRate, true);
  bytes.setUint32(28, buffer.sampleRate * 2, true);
  bytes.setUint16(32, 2, true);
  bytes.setUint16(34, 16, true);
  text(36, 'data');
  bytes.setUint32(40, data.length * 2, true);
  for (let i = 0; i < data.length; i++) {
    bytes.setInt16(44 + i * 2, Math.max(-1, Math.min(1, data[i]! * scale)) * 0x7fff, true);
  }
  return new Blob([bytes], { type: 'audio/wav' });
}
